import jsPDF from 'jspdf';
import { formatCurrency, formatLocation } from '@/lib/formatters';
import { SpeakerDetailsInterface } from '@/types/SpeakerDetailsInterface';

export const generateSpeakerBioPDF = (speaker: SpeakerDetailsInterface) => {
  const pdf = new jsPDF();
  let yPosition = 20;
  const pageWidth = pdf.internal.pageSize.width;
  const margin = 20;
  const contentWidth = pageWidth - (margin * 2);

  // Helper function to add text with automatic line breaks
  const addText = (text: string, fontSize: number = 12, isBold: boolean = false) => {
    pdf.setFontSize(fontSize);
    if (isBold) {
      pdf.setFont('helvetica', 'bold');
    } else {
      pdf.setFont('helvetica', 'normal');
    }
    
    const lines = pdf.splitTextToSize(text, contentWidth);
    pdf.text(lines, margin, yPosition);
    yPosition += (lines.length * fontSize * 0.35) + 5;
    
    return lines.length;
  };

  // Header
  addText(`SPEAKER PROFILE`, 10, false);
  addText(speaker.name, 22, true);
  addText(`https://shespeakscyber.org/speakers/${speaker.slug}`, 14, false);
  yPosition += 5;

  // Basic Info
  addText('BASIC INFORMATION', 14, true);
  addText(`Name: ${speaker.name}`);
  addText(`Title: ${speaker.title}`);
  addText(`Location: ${formatLocation(speaker.hometown, speaker.country)}`);
//   if (speaker.email) {
//     addText(`Email: ${speaker.email}`);
//   }
  if (speaker.linkedinUrl) {
    addText(`LinkedIn: ${speaker.linkedinUrl}`);
  }
  if (speaker.website) {
    addText(`Website: ${speaker.website}`);
  }
  yPosition += 10;

  // Bio
  addText('BIOGRAPHY', 14, true);
  addText(speaker.bio);
  yPosition += 10;

  // Areas of Expertise
  addText('AREAS OF EXPERTISE', 14, true);
//   addText(speaker.topics.join(', '));
    speaker.topics.forEach((topic, index) => {
      addText(topic.name);
      
      // Check if we need a new page
      if (yPosition > 250) {
        pdf.addPage();
        yPosition = 20;
      }
    });
  yPosition += 10;

  // Languages
  addText('LANGUAGES', 14, true);
  addText(speaker.languages.join(', '));
  yPosition += 10;

  // Travel Availability
  addText('TRAVEL AVAILABILITY', 14, true);
  let travelText = '';
  switch (speaker.travelAvailability) {
    case 'local':
      travelText = 'Local events only';
      break;
    case 'regional':
      travelText = 'Regional events';
      break;
    case 'global':
      travelText = 'Global events';
      break;
  }
  addText(travelText);
  yPosition += 10;

  // Check if we need a new page
  if (yPosition > 240) {
    pdf.addPage();
    yPosition = 20;
  }

  // Available Services
  addText('AVAILABLE SERVICES', 14, true);
  const services = [];
  if (speaker.availableForKeynotes) services.push('Keynotes');
  if (speaker.availableForWorkshops) services.push('Workshops');
  if (speaker.availableForPanels) services.push('Panels');
  if (speaker.availableForExecutiveBriefings) services.push('Executive Briefings');
  if (speaker.availableForExercises) services.push('Training Exercises');
  if (speaker.availableForVirtualEvents) services.push('Virtual Events');
  
  addText(services.join(', '));
  yPosition += 10;

  // Check if we need a new page
  if (yPosition > 240) {
    pdf.addPage();
    yPosition = 20;
  }

  // Standard Talks
  if (speaker.standardTalks.length > 0) {
    addText('STANDARD TALKS', 14, true);
    speaker.standardTalks.forEach((talk, index) => {
      addText(`${index + 1}. ${talk.title}`, 12, true);
      addText(talk.description);
      yPosition += 5;
      
      // Check if we need a new page
      if (yPosition > 240) {
        pdf.addPage();
        yPosition = 20;
      }
    });
    yPosition += 5;
  }

  // Speaking Fees
  addText('SPEAKING FEES', 14, true);
  let feesText = '';
  if (speaker.fees.paidTalks && speaker.fees.startingPrice) {
    feesText += `Starting price: ${formatCurrency(speaker.fees.startingPrice)}\n`;
  }
  if (speaker.fees.communityTalks) {
    feesText += 'Available for community events\n';
  }
  feesText += `Travel expenses: ${speaker.fees.travelExpensesOnly ? 'Travel expenses requested' : 'Included in fee'}`;
  addText(feesText);
  yPosition += 10;

  // Past Speaking Engagements
  if (speaker.pastEvents.length > 0) {
    // Check if we need a new page
    if (yPosition > 200) {
      pdf.addPage();
      yPosition = 20;
    }
    
    addText('PAST SPEAKING ENGAGEMENTS', 14, true);
    speaker.pastEvents.forEach((event, index) => {
      addText(`${index + 1}. ${event.name} (${event.year})`, 12, true);
      addText(`Presentation: ${event.presentationTitle}`);
      addText(`Type: ${event.type} | Audience: ${event.audience}`);
      if (event.eventUrl) {
        addText(`Event URL: ${event.eventUrl}`);
      }
      if (event.videoUrl) {
        addText(`Video: ${event.videoUrl}`);
      }
      yPosition += 5;
      
      // Check if we need a new page
      if (yPosition > 250) {
        pdf.addPage();
        yPosition = 20;
      }
    });
  }

  // Footer
  const currentDate = new Date().toLocaleDateString();
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Generated on ${currentDate}`, margin, pdf.internal.pageSize.height - 10);

  // Save the PDF
  pdf.save(`${speaker.name.replace(/\s+/g, '_')}_Speaker_Bio.pdf`);
};